import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ShieldOff, MailCheck, Loader2 } from "lucide-react";
import { authApi, formatApiError } from "../../lib/authApi";
import { setMemberSession } from "../../hooks/useMemberAuth";
import { AuthShell, Field, PrimaryBtn, SmallLink, Notice } from "../../components/auth/AuthShell";
import SocialButtons from "../../components/auth/SocialButtons";

/**
 * LoginPage — the sign-in gate to the Archive.
 *
 * On success, always routes through the cinematic Access-Granted
 * reveal (never straight back to a public page — membership must
 * feel like unlocking something), then to /members.
 *
 * On failure, stays on this screen and swaps in one of two
 * archival status states, mirroring the design brief:
 *
 *   • ACCESS DENIED — bad password / unknown email.
 *     Keeps the email field populated. Clears only the password.
 *     Allows immediate retry.
 *
 *   • VERIFICATION REQUIRED — the account exists but the email
 *     has not been verified yet. Shows a working "Resend
 *     Verification Email" button (60 s cooldown enforced by the
 *     backend); no password retry until that's cleared.
 */

const LoginPage = () => {
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState({ kind: "idle" });
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMsg, setResendMsg] = useState(null);

  // Members-only routes are the ONLY valid post-login destination.
  // If the caller asked for a public page (or nothing), we override
  // to /members so no successful sign-in ever silently dumps the
  // user back to the anonymous archive homepage.
  const nextParam = new URLSearchParams(location.search).get("next") || "";
  const isMembersScoped = /^\/(members|inbox|member\/|dispatch|submit|admin)/.test(nextParam);
  const nextPath = isMembersScoped ? nextParam : "/members";

  const detectUnverified = (status, msg) => {
    // Backend signals unverified accounts with 403 + a message
    // beginning "Verify your email to sign in".
    if (status !== 403) return false;
    if (typeof msg !== "string") return false;
    const m = msg.toLowerCase();
    return m.includes("verify your email") || m.includes("verification");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ kind: "idle" });
    setResendMsg(null);
    setBusy(true);
    try {
      const { data } = await authApi.login({ email, password, remember_me: remember });
      if (data.status === "set_password_required") {
        setState({ kind: "info", message: data.message || "Check your inbox for a set-password link." });
        return;
      }
      if (data.status === "two_factor_required") {
        setState({ kind: "info", message: "Two-factor required (not yet available)." });
        return;
      }
      await setMemberSession(data.session_token);
      // Always cinematic — user brief: "Display a full-screen
      // cinematic transition" on successful auth. Repeat visitors
      // get the same moment; membership should never feel silent.
      nav(`/auth/access-granted?welcome=1&next=${encodeURIComponent(nextPath)}`);
    } catch (e) {
      // Clear ONLY password on failure — keep email so the visitor
      // can retry immediately without retyping.
      setPassword("");
      const status = e?.response?.status;
      const rawDetail = e?.response?.data?.detail;
      const msg = formatApiError(rawDetail) || "Access denied.";
      if (detectUnverified(status, msg)) {
        setState({ kind: "unverified", message: msg });
      } else {
        setState({ kind: "denied", message: msg });
      }
    } finally {
      setBusy(false);
    }
  };

  const onResend = async () => {
    if (!email || resendBusy) return;
    setResendBusy(true);
    setResendMsg(null);
    try {
      await authApi.resendVerification(email);
      setResendMsg({ tone: "ok", text: "Verification email dispatched. Check your inbox — including spam." });
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || "Could not resend right now.";
      setResendMsg({ tone: "err", text: msg });
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <AuthShell
      title={state.kind === "denied" ? "Access denied" :
             state.kind === "unverified" ? "Verification required" :
             "Sign in"}
      lead={state.kind === "denied"
        ? "The credentials submitted do not match any active operative record. Verify the email address, then retry."
        : state.kind === "unverified"
        ? "This operative file exists but the email address hasn't been confirmed yet. Verify it to activate clearance."
        : "Access your private Archive workspace. Email + password required. Sessions are encrypted and revocable."}
      footer={<>Don&apos;t have an account? <SmallLink to="/auth/signup" data-testid="login-link-signup">Create one</SmallLink></>}
    >
      {state.kind === "denied" && (
        <div
          data-testid="login-access-denied"
          className="border p-4 mb-5 flex gap-3 items-start"
          style={{ borderColor: "#D92D2066", backgroundColor: "rgba(217,45,32,0.06)" }}
        >
          <span aria-hidden style={{ color: "#D92D20", marginTop: 1 }}><ShieldOff size={16} /></span>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.4em]" style={{ color: "#D92D20" }}>
              // ACCESS DENIED
            </div>
            <div className="font-mono text-[11px] leading-relaxed mt-1" style={{ color: "#E4DFD3" }}>
              {state.message}
            </div>
            <div className="font-mono text-[9px] uppercase tracking-[0.35em] mt-2" style={{ color: "#6F6A60" }}>
              Retry authorised · Password field cleared
            </div>
          </div>
        </div>
      )}
      {state.kind === "unverified" && (
        <div
          data-testid="login-verification-required"
          className="border p-4 mb-5"
          style={{ borderColor: "#E0A52666", backgroundColor: "rgba(224,165,38,0.06)" }}
        >
          <div className="flex gap-3 items-start">
            <span aria-hidden style={{ color: "#E0A526", marginTop: 1 }}><MailCheck size={16} /></span>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[10px] uppercase tracking-[0.4em]" style={{ color: "#E0A526" }}>
                // VERIFICATION REQUIRED
              </div>
              <div className="font-mono text-[11px] leading-relaxed mt-1" style={{ color: "#E4DFD3" }}>
                {state.message}
              </div>
              <button
                type="button"
                onClick={onResend}
                disabled={resendBusy || !email}
                data-testid="login-resend-verification"
                className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.35em] px-3 py-2 border disabled:opacity-50"
                style={{ borderColor: "#E0A526", color: "#E0A526", backgroundColor: "rgba(224,165,38,0.08)" }}
              >
                {resendBusy && <Loader2 size={12} className="animate-spin" />}
                {resendBusy ? "DISPATCHING…" : "RESEND VERIFICATION EMAIL"}
              </button>
              {resendMsg && (
                <div
                  data-testid="login-resend-result"
                  className="font-mono text-[10px] leading-relaxed mt-3"
                  style={{ color: resendMsg.tone === "ok" ? "#5DD4B3" : "#D92D20" }}
                >
                  {resendMsg.text}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {state.kind === "info" && (
        <Notice kind="info" data-testid="login-info">{state.message}</Notice>
      )}
      <SocialButtons next={nextPath} disabled={busy} />
      <form onSubmit={onSubmit} data-testid="login-form">
        <Field label="EMAIL" type="email" value={email} onChange={setEmail}
               required autoComplete="email" autoFocus
               data-testid="login-email" name="email" />
        <Field label="PASSWORD" type="password" value={password} onChange={setPassword}
               required autoComplete="current-password"
               data-testid="login-password" name="password" />
        <div className="flex items-center justify-between mb-5">
          <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] flex items-center gap-2">
            <input type="checkbox" checked={remember}
                   onChange={(e) => setRemember(e.target.checked)}
                   data-testid="login-remember" />
            REMEMBER ME
          </label>
          <SmallLink to="/auth/forgot-password" data-testid="login-forgot">FORGOT?</SmallLink>
        </div>
        <PrimaryBtn disabled={busy} data-testid="login-submit">
          {busy ? "AUTHENTICATING…" : "SIGN IN"}
        </PrimaryBtn>
      </form>
    </AuthShell>
  );
};

export default LoginPage;
